import { useState, useEffect, useRef, useMemo } from 'react'
import {
  collection,
  onSnapshot,
  setDoc,
  query,
  addDoc,
  serverTimestamp,
  doc,
  where,
  orderBy,
  documentId,
} from 'firebase/firestore'

import * as Sentry from '@sentry/react'
import { db } from '../firebase'
import { receptionStateValues, receptionStatusValues } from 'constants/receptions'
import { useCollectionData } from 'react-firebase-hooks/firestore'
import { receptionConverter } from 'conventers/receptionConventer'
import { omit, omitBy, isUndefined, chunk, isPlainObject, mapValues } from 'lodash'
import { useMultiQueryData } from 'hooks/useMultiQueryData'
import { useAddChangelogs } from 'data/changelogs'
import { changelogTypes } from 'constants/changelogs'

/**
 * Recursively removes all undefined values from an object and its nested objects.
 * This is needed because Firestore doesn't allow undefined values.
 * @param {*} obj - The object to clean
 * @returns {*} - The cleaned object with no undefined values
 */
const deepOmitUndefined = obj => {
  if (!isPlainObject(obj)) {
    return obj
  }
  // First, recursively clean nested objects
  const cleaned = mapValues(obj, value => {
    if (isPlainObject(value)) {
      return deepOmitUndefined(value)
    }
    return value
  })
  // Then remove undefined values at this level
  return omitBy(cleaned, isUndefined)
}

/**
 * Get product with id. Also the data keeps updating if it changes in Firestore.
 *
 * TODO: Add loading and error properties to the return value.
 *
 * @param {boolean} getData Set to false if you don't want to get receptions list
 * @returns {object} Containing receptions data
 */
export function useReceptions(getData = true) {
  // Store receptions data to local state
  const [receptions, setReceptions] = useState([])
  const [loading, setLoading] = useState(true)
  const addChangelog = useAddChangelogs()

  // Sync product data
  useEffect(() => {
    if (!getData) return setLoading(false)
    setLoading(true)

    const q = query(
      // Reference (or path) to the data in Firestore.
      collection(db, 'receptions'),
      where('deletedAt', '==', null),
      orderBy('serialNumber', 'desc')
    )

    // Time to the first snapshot of the whole receptions list. Sentry does not
    // instrument Firestore listeners, and this list is loaded by several pages.
    let loadSpan = Sentry.startInactiveSpan({ name: 'receptions.load', op: 'firestore.snapshot' })
    let loaded = false
    const endLoadSpan = count => {
      if (count !== undefined) loadSpan?.setAttribute('count', count)
      loadSpan?.end()
      loadSpan = null
    }

    // Subscribe to data from /receptions
    const unsubscribe = onSnapshot(
      // Refetence (or path) to the data in Firestore.
      q,
      // This callback is called when data is initially fetched and when it changes.
      querySnapshot => {
        // On a slow or flaky connection the SDK first delivers a snapshot from
        // its in-memory cache, holding only what other listeners happened to
        // load (e.g. the product's own reception). Wait for the real list.
        if (!loaded && querySnapshot.metadata.fromCache) return
        loaded = true
        endLoadSpan(querySnapshot.size)
        setLoading(false)
        const data = []
        querySnapshot.forEach(doc => {
          data.push({ ...doc.data(), id: doc.id })
        })

        // Set receptions data to local state in this hook.
        setReceptions(data)
      },
      error => {
        loadSpan?.setStatus({ code: 2, message: 'error' })
        endLoadSpan()
        console.error('Failed to sync Product data', error)
      }
    )

    return () => {
      // Unsubscript to change callbacks when the hook unmounts
      endLoadSpan()
      unsubscribe()
    }
  }, [getData])

  // These functions mutate data in Firestore.
  const mutationsRef = useRef(
    {
      // Update reception in Firestore.
      setReception: (data, receptionId) => {
        const receptionDocRef = doc(db, 'receptions', receptionId)
        return addChangelog(receptionDocRef, changelogTypes.UPDATE, () =>
          setDoc(
            receptionDocRef,
            {
              // keep legacy behavior: never write internalNote to the root doc
              // also remove undefineds recursively to avoid Firestore validation errors
              ...deepOmitUndefined(omit(data, 'createdAt', 'purchaseInvoiceCreatedAt', 'updatedAt', 'internalNote')),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          )
        )
      },
      softDeleteReception: receptionId => {
        const receptionDocRef = doc(db, 'receptions', receptionId)
        return addChangelog(receptionDocRef, changelogTypes.DELETE, () =>
          setDoc(
            doc(db, 'receptions', receptionId),
            {
              deletedAt: serverTimestamp(),
            },
            { merge: true }
          )
        )
      },
      addReception: async data => {
        const receptionDocRef = await addDoc(collection(db, 'receptions'), {
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          deletedAt: null,
          ...data,
        })

        await addChangelog(receptionDocRef, changelogTypes.CREATE)

        return receptionDocRef
      },
    },
    []
  )

  return {
    // Receptions data from Firebase
    receptions,
    loading,

    // Helper
    getSeller: () => receptions?.seller,

    // Spread Firestore mutation functions.
    ...mutationsRef.current,
  }
}

/**
 * Subscribe to a single reception. Use this instead of useReceptions() when
 * the page only needs one document; the full list is 3,000+ documents.
 *
 * @param {string} receptionId
 * @returns {{ reception: object | null, loading: boolean }}
 */
export function useReception(receptionId) {
  const [reception, setReception] = useState(null)
  const [loading, setLoading] = useState(!!receptionId)

  useEffect(() => {
    if (!receptionId) {
      setReception(null)
      setLoading(false)
      return
    }
    setLoading(true)

    // Time to the first snapshot; Sentry does not instrument Firestore listeners.
    let loadSpan = Sentry.startInactiveSpan({
      name: 'reception.load',
      op: 'firestore.snapshot',
      attributes: { receptionId },
    })
    const endLoadSpan = () => {
      loadSpan?.end()
      loadSpan = null
    }

    const unsubscribe = onSnapshot(
      doc(db, 'receptions', receptionId),
      snapshot => {
        endLoadSpan()
        const data = snapshot.data()
        // Soft-deleted receptions are hidden, like in the list hook.
        setReception(snapshot.exists() && data.deletedAt == null ? { ...data, id: snapshot.id } : null)
        setLoading(false)
      },
      error => {
        loadSpan?.setStatus({ code: 2, message: 'error' })
        endLoadSpan()
        console.error('Failed to sync reception', error)
        setLoading(false)
      }
    )

    return () => {
      endLoadSpan()
      unsubscribe()
    }
  }, [receptionId])

  return { reception, loading }
}

export function useReceptionsByIds(ids) {
  // Store receptions data to local state
  const queries = useMemo(
    () =>
      chunk(ids || [], 10).map(ids => {
        return query(collection(db, `receptions`), where(documentId(), 'in', ids))
      }),
    [ids]
  )
  const [receptions, loading] = useMultiQueryData(...queries)

  return {
    // Receptions data from Firebase
    receptions,
    loading,
  }
}

export function useClientReceptions(id) {
  // Store receptions data to local state
  const q = useMemo(() => {
    if (!id) return undefined

    return query(
      // Reference (or path) to the data in Firestore.
      collection(db, 'receptions'),
      where('deletedAt', '==', null),
      where('sellerId', '==', id)
    ).withConverter(receptionConverter)
  }, [id])

  const [receptions, loading, error] = useCollectionData(q)

  useEffect(() => {
    if (error) console.error(error)
  }, [error])

  return {
    // Receptions data from Firebase
    receptions,
    loading,
    error,
  }
}

export const useApprovedReceptions = () => {
  const [receptions = [], loading, error] = useCollectionData(
    query(
      collection(db, 'receptions'),
      where('status', 'in', [receptionStatusValues.APPROVED, receptionStatusValues.COMPENSATED]),
      where('deletedAt', '==', null),
      orderBy('createdAt', 'desc')
    ).withConverter(receptionConverter)
  )

  useEffect(() => {
    if (error) {
      console.error(error)
    }
  }, [error])

  return [receptions, loading]
}

export const useOpenReceptionsForAssignedUser = email => {
  const [receptions = [], loading, error] = useCollectionData(
    (email &&
      query(
        collection(db, 'receptions'),
        where('personnelAssigned', '==', email),
        where('state', '==', receptionStateValues.OPEN),
        where('deletedAt', '==', null),
        orderBy('createdAt', 'desc')
      ).withConverter(receptionConverter)) ||
      undefined
  )

  useEffect(() => {
    if (error) {
      console.error(error)
    }
  }, [error])

  return [receptions, loading]
}

const useReceptionsForAssignedUserByStatus = (email, status) => {
  const [receptions = [], loading, error] = useCollectionData(
    (email &&
      query(
        collection(db, 'receptions'),
        where('personnelAssigned', '==', email),
        where('status', '==', status),
        where('state', '==', receptionStateValues.OPEN),
        where('deletedAt', '==', null),
        orderBy('createdAt', 'desc')
      ).withConverter(receptionConverter)) ||
      undefined
  )

  useEffect(() => {
    if (error) {
      console.error(error)
    }
  }, [error])

  return [receptions, loading]
}

export const usePendingReceptionsForAssignedUser = email =>
  useReceptionsForAssignedUserByStatus(email, receptionStatusValues.PENDING)

export const useReadyToSendReceptionsForAssignedUser = email =>
  useReceptionsForAssignedUserByStatus(email, receptionStatusValues.READY_TO_SEND)

export const useOpenReceptions = () => {
  const [receptions = [], loading, error] = useCollectionData(
    query(
      collection(db, 'receptions'),
      where('state', '==', receptionStateValues.OPEN),
      where('deletedAt', '==', null),
      orderBy('createdAt', 'desc')
    ).withConverter(receptionConverter)
  )

  useEffect(() => {
    if (error) {
      console.error(error)
    }
  }, [error])

  return [receptions, loading]
}

export const useOpenReceptionsForExperts = uid => {
  const [receptions = [], loading, error] = useCollectionData(
    (uid &&
      query(
        collection(db, 'receptions'),
        where('experts', 'array-contains', uid),
        where('state', '==', receptionStateValues.OPEN),
        where('deletedAt', '==', null),
        orderBy('createdAt', 'desc')
      ).withConverter(receptionConverter)) ||
      undefined
  )

  useEffect(() => {
    if (error) {
      console.error(error)
    }
  }, [error])

  return [receptions, loading]
}

export const useReceptionsByState = state => {
  const [receptions, loading, error] = useCollectionData(
    state &&
      query(
        collection(db, 'receptions'),
        where('state', '==', state),
        where('deletedAt', '==', null),
        orderBy('serialNumber', 'desc')
      ).withConverter(receptionConverter)
  )

  useEffect(() => {
    if (error) console.error(error)
  }, [error])

  return { receptions, loading, error }
}
